//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import DeviceMovementModel from './deviceMovement.model';

/**
 * @service DeviceMovementService
 * @description Service for managing device movement and assignment history.
 * This service provides HTTP methods to record the movement of IoT devices between
 * different locations, such as from a warehouse to a farm, or de-assigning a device.
 * It is crucial for maintaining an accurate audit trail of device logistics.
 * Extends the base Service class with the generic type DeviceMovementModel.
 *
 * @feature 1. Create device movement records (e.g., for de-assignment).
 * @feature 2. Track and cancel ongoing HTTP requests to prevent memory leaks.
 * @feature 3. Promise-based async operations for device logistics.
 *
 * @architecture
 * Extends the base `Service<DeviceMovementModel>` to provide standardized REST API
 * integration with the `/aqu/devicemovement/` endpoint. It uses Angular's `HttpClient`
 * for all HTTP operations and manages subscriptions in an `ongoingRequests` array
 * for easy cancellation, ensuring robust resource management.
 *
 * @service-endpoint /aqu/devicemovement/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceMovementService extends Service<DeviceMovementModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * This is used to manage and cancel pending requests, for example, when a component is destroyed.
   * @default []
   * @side-effect Stores subscription references, which are later used by `cancelOnGoingHttpRequests()`.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor that injects the HttpClient and sets up the service endpoint.
   *
   * Calls the parent `Service` constructor, passing the `HttpClient` instance and the
   * specific API endpoint path for device movement operations.
   *
   * @param {HttpClient} httpClient - @service Angular's HTTP client for making API requests.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/devicemovement/');
  }

  /**
   * @method deassignFun
   * @async
   * @description Creates a device movement record, often used to log a de-assignment action.
   *
   * This method sends a `DeviceMovementModel` object to the `addDeviceMovement` endpoint
   * via an HTTP POST request. This is used to create a historical record of a device's
   * change in assignment or location.
   *
   * @param {DeviceMovementModel} deviceMovement - The device movement model containing details of the action.
   * @returns {Promise<DeviceMovementModel>} A promise that resolves with the created device movement record.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates a new device movement record in the database. Adds the request subscription to `ongoingRequests`.
   *
   * @example
   * const movement = new DeviceMovementModel();
   * movement.device_id = 'device-123';
   * movement.action_name = 'De-assigned from Farm';
   * movement.comment = 'Device returned for maintenance.';
   *
   * try {
   *   const result = await deviceMovementService.deassignFun(movement);
   *   console.log('Movement recorded:', result);
   * } catch (error) {
   *   console.error('Failed to record movement:', error);
   * }
   */
  public deassignFun(deviceMovement: DeviceMovementModel) {
    return new Promise<DeviceMovementModel>((resolve, reject) => {
      const request = this.httpClient.post<DeviceMovementModel>(`${this.baseUrl}${this.serviceUrl}addDeviceMovement`, deviceMovement, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  public getAllDeviceTrackingBySerialNumber(device_serial_number: string) {
    return new Promise<Array<DeviceMovementModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<DeviceMovementModel>>(`${this.baseUrl}${this.serviceUrl}serialNumber/${device_serial_number}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancels all ongoing/pending HTTP requests initiated by this service.
   *
   * Iterates through the `ongoingRequests` array and unsubscribes from each active
   * subscription. This is a crucial cleanup step to prevent memory leaks and
   * race conditions when a component is destroyed.
   *
   * @returns {void}
   * @side-effect Aborts all pending HTTP requests and clears the `ongoingRequests` array.
   * @usage This method should be called in the `ngOnDestroy` lifecycle hook of any
   * component that uses this service to ensure proper cleanup.
   * @example
   * ngOnDestroy() {
   *   this.deviceMovementService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
